import { Injectable } from '@angular/core';
import { SignInWithAppleResponse } from '@capacitor-community/apple-sign-in';

import { CompleteRegistrationBody, RegisterBody } from '@reach/interfaces/auth/auth.requests';
import {
	CompleteRegistrationResponse,
	LoginResponse,
	LogoutResponse,
	NewPasswordResponse,
	RecoverPasswordResponse,
	RegisterResponse,
	ResendVerifyEmailResponse,
	VerifyEmailResponse,
} from '@reach/interfaces/auth/auth.responses';
import { ReachHeaderEnum } from '@reach/interfaces/headers/reach-header.enum';
import { Language } from '@reach/interfaces/user/language.enum';
import { UserInPing } from '@reach/interfaces/user/user-in-ping';

import { BaseApiService } from '~app-client/api/_internal';
import { userTransformer } from '~app-client/core/transformers';
import { ClientUser } from '~app-client/core/types';

export interface SocialUserRequest {
	email: string;
	name: string;
	idToken: string;
	photoUrl: string;
	category?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthApiService extends BaseApiService {
	protected commonPath = ['auth'];

	public async ping(): Promise<ClientUser> {
		const params = { t: this.paramTime };

		const res = await this.http.getWithHeaders<UserInPing>(this.url('ping'), params);

		if (res.body === null) {
			throw 'empty body';
		}

		const impersonatingValue =
			res.headers.get(ReachHeaderEnum.HEADER_REACH_IMPERSONATING) ?? '';
		const impersonating = impersonatingValue === 'true';

		return userTransformer(res.body, { impersonating });
	}

	public async login(usernameOrEmail: string, password: string): Promise<ClientUser> {
		return await this.http
			.post<LoginResponse>(this.url('login'), {
				username: usernameOrEmail,
				password,
			})
			.then(userTransformer);
	}

	public async register(user: RegisterBody): Promise<ClientUser> {
		const body = user;
		return await this.http
			.post<RegisterResponse>(this.url('register'), body)
			.then(userTransformer);
	}

	public async resetPassword(email?: string): Promise<RecoverPasswordResponse> {
		const body = { email };
		return await this.http.post<RecoverPasswordResponse>(this.url('password-recovery'), body);
	}

	public async changePassword(newPassword: string, token: string): Promise<NewPasswordResponse> {
		return await this.http.patch<NewPasswordResponse>(
			this.url('password'),
			{ newPassword },
			{},
			{ ['x-access-token']: token }
		);
	}

	public async logout(): Promise<LogoutResponse> {
		return await this.http.post<LogoutResponse>(this.url('logout'));
	}

	public async signInWithGoogle(
		googleToken: string,
		language: Language,
		referralId?: string
	): Promise<ClientUser> {
		return await this.http
			.post<LoginResponse>(this.url('social', 'google'), {
				googleToken,
				language,
				referralId,
			})
			.then(userTransformer);
	}

	public async signInWithApple(
		result: SignInWithAppleResponse,
		language: Language,
		referralId?: string
	): Promise<ClientUser> {
		const body = {
			idToken: result.response.identityToken,
			name: result.response.givenName,
			lastName: result.response.familyName,
			language,
			referralId,
		};
		return await this.http
			.post<LoginResponse>(this.url('social', 'apple'), body)
			.then(userTransformer);
	}

	public async signInWithMicrosoft(
		accessToken: string,
		language: Language,
		referralId?: string
	): Promise<ClientUser> {
		const body = {
			accessToken,
			language,
			referralId,
		};
		return await this.http
			.post<LoginResponse>(this.url('social', 'azure'), body)
			.then(userTransformer);
	}

	public async completeRegistration(data: CompleteRegistrationBody): Promise<ClientUser> {
		return await this.http
			.post<CompleteRegistrationResponse>(this.url('complete-registration'), data)
			.then(userTransformer);
	}

	public async resendConfirmationEmail(): Promise<ResendVerifyEmailResponse> {
		return await this.http.post<ResendVerifyEmailResponse>(
			this.url('resend-confirmation-email')
		);
	}

	public async verifyEmail(token: string): Promise<VerifyEmailResponse> {
		return await this.http.patch<VerifyEmailResponse>(
			this.url('verify-email'),
			{ emailVerified: true },
			{},
			{ 'x-access-token': token }
		);
	}
}
